(() => {
  const init = () => {
    const sections = document.querySelectorAll('.cgrid');
    if (!sections.length) return;

    const normalize = (value) =>
      String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

    const parseMoneyInput = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return 0;
      const normalized = raw
        .replace(/\s/g, '')
        .replace(/[^\d,.-]/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
      const parsed = Number.parseFloat(normalized);
      if (Number.isNaN(parsed)) return 0;
      return Math.round(parsed * 100);
    };

    sections.forEach((section) => {
      if (section.dataset.cgridFiltersReady === 'true') return;
      section.dataset.cgridFiltersReady = 'true';

      const grid = section.querySelector('[data-product-grid]');
      const cards = Array.from(section.querySelectorAll('.cgrid-card'));
      const countNode = section.querySelector('[data-results-count]');
      const emptyState = section.querySelector('[data-empty-state]');
      const minInput = section.querySelector('[data-price-input="min"]');
      const maxInput = section.querySelector('[data-price-input="max"]');
      const minRange = section.querySelector('[data-price-range="min"]');
      const maxRange = section.querySelector('[data-price-range="max"]');
      const priceTrack = section.querySelector('[data-price-track]');
      const priceReadout = section.querySelector('[data-price-readout]');
      const currencyCode = section.dataset.currencyCode || 'BRL';

      const drawer = section.querySelector('[data-filter-drawer]');
      const openButton = section.querySelector('[data-filter-open]');
      const closeButtons = section.querySelectorAll('[data-filter-close]');
      const clearButton = section.querySelector('[data-filter-clear]');
      const applyButton = section.querySelector('[data-filter-apply]');
      const activeCountNode = section.querySelector('[data-active-filter-count]');
      const applyCountNode = section.querySelector('[data-apply-count]');
      const sortSelect = section.querySelector('[data-sort]');

      const formatMoney = (valueInCents) =>
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format((valueInCents || 0) / 100);

      const updateTrack = () => {
        if (!minRange || !maxRange || !priceTrack) return;
        const min = Number(minRange.min || 0);
        const max = Number(maxRange.max || 0);
        const start = ((Number(minRange.value) - min) / (max - min || 1)) * 100;
        const end = ((Number(maxRange.value) - min) / (max - min || 1)) * 100;
        priceTrack.style.setProperty('--range-start', `${start}%`);
        priceTrack.style.setProperty('--range-end', `${end}%`);
      };

      const updatePriceReadout = () => {
        if (!priceReadout || !minRange || !maxRange) return;
        priceReadout.textContent = `Preço: ${formatMoney(Number(minRange.value))} – ${formatMoney(Number(maxRange.value))}`;
      };

      const syncInputsFromRanges = () => {
        if (!minInput || !maxInput || !minRange || !maxRange) return;
        minInput.value = formatMoney(Number(minRange.value));
        maxInput.value = formatMoney(Number(maxRange.value));
        updateTrack();
        updatePriceReadout();
      };

      const syncRangesFromInputs = () => {
        if (!minInput || !maxInput || !minRange || !maxRange) return;
        const rangeMin = Number(minRange.min || 0);
        const rangeMax = Number(maxRange.max || 0);
        const minValue = Math.max(rangeMin, parseMoneyInput(minInput.value));
        const maxValue = Math.min(rangeMax, parseMoneyInput(maxInput.value));
        const safeMin = Math.min(minValue, maxValue || rangeMax);
        const safeMax = Math.max(maxValue || rangeMax, safeMin);
        minRange.value = String(safeMin);
        maxRange.value = String(safeMax);
        syncInputsFromRanges();
      };

      const getSelectedValues = (group) =>
        Array.from(section.querySelectorAll(`[data-filter-group="${group}"]:checked`)).map((input) =>
          normalize(input.value)
        );

      const countActiveFilters = () => {
        const checkboxes = section.querySelectorAll('[data-filter-group]:checked').length;
        let priceActive = 0;
        if (minRange && maxRange) {
          const touchedMin = Number(minRange.value) > Number(minRange.min);
          const touchedMax = Number(maxRange.value) < Number(maxRange.max);
          priceActive = touchedMin || touchedMax ? 1 : 0;
        }
        return checkboxes + priceActive;
      };

      const updateFilterBadges = () => {
        const total = countActiveFilters();
        if (activeCountNode) {
          activeCountNode.textContent = total;
          activeCountNode.hidden = total === 0;
        }
        if (applyCountNode) {
          applyCountNode.textContent = total > 0 ? ` (${total})` : '';
        }
      };

      const applyFilters = () => {
        const types = getSelectedValues('type');
        const brands = getSelectedValues('brand');
        const colors = getSelectedValues('color');
        const sizes = getSelectedValues('size');
        const materials = getSelectedValues('material');
        const minPrice = minRange ? Number(minRange.value || minRange.min || 0) : 0;
        const maxPrice = maxRange ? Number(maxRange.value || maxRange.max || 0) : Number.MAX_SAFE_INTEGER;

        let visibleCount = 0;

        cards.forEach((card) => {
          const cardType = normalize(card.dataset.type);
          const cardBrand = normalize(card.dataset.brand);
          const cardMaterial = normalize(card.dataset.material);
          const cardColors = normalize(card.dataset.colors).split('|').filter(Boolean);
          const cardSizes = normalize(card.dataset.sizes).split('|').filter(Boolean);
          const cardPrice = Number(card.dataset.price || 0);

          const matchesType = types.length === 0 || types.includes(cardType);
          const matchesBrand = brands.length === 0 || brands.includes(cardBrand);
          const matchesMaterial = materials.length === 0 || materials.includes(cardMaterial);
          const matchesColor = colors.length === 0 || colors.some((value) => cardColors.includes(value));
          const matchesSize = sizes.length === 0 || sizes.some((value) => cardSizes.includes(value));
          const matchesPrice = cardPrice >= minPrice && cardPrice <= maxPrice;

          const isVisible =
            matchesType && matchesBrand && matchesMaterial && matchesColor && matchesSize && matchesPrice;
          card.classList.toggle('hidden', !isVisible);
          if (isVisible) visibleCount += 1;
        });

        if (countNode) {
          countNode.textContent = `${visibleCount} peça${visibleCount === 1 ? '' : 's'}`;
        }
        if (emptyState) {
          emptyState.classList.toggle('hidden', visibleCount !== 0);
        }
        updateFilterBadges();
      };

      const sortCards = () => {
        if (!grid || !sortSelect) return;
        const mode = sortSelect.value;
        const sorted = [...cards];

        sorted.sort((a, b) => {
          switch (mode) {
            case 'price-ascending':
              return Number(a.dataset.price) - Number(b.dataset.price);
            case 'price-descending':
              return Number(b.dataset.price) - Number(a.dataset.price);
            case 'title-ascending':
              return String(a.dataset.title).localeCompare(String(b.dataset.title), 'pt-BR');
            case 'title-descending':
              return String(b.dataset.title).localeCompare(String(a.dataset.title), 'pt-BR');
            case 'created-descending':
              return Number(b.dataset.date) - Number(a.dataset.date);
            default:
              return Number(a.dataset.index) - Number(b.dataset.index);
          }
        });

        sorted.forEach((card) => grid.appendChild(card));
      };

      section.querySelectorAll('[data-grid-size-button]').forEach((button) => {
        button.addEventListener('click', () => {
          const nextSize = button.getAttribute('data-grid-size-button');
          if (!nextSize) return;
          section.dataset.gridSize = nextSize;
          section.querySelectorAll('[data-grid-size-button]').forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
        });
      });

      let lastFocused = null;
      const openDrawer = () => {
        if (!drawer) return;
        lastFocused = document.activeElement;
        drawer.hidden = false;
        requestAnimationFrame(() => drawer.classList.add('is-open'));
        document.body.style.overflow = 'hidden';
        drawer.querySelector('.cgrid-drawer__close')?.focus();
      };
      const closeDrawer = () => {
        if (!drawer) return;
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        const onEnd = () => {
          drawer.hidden = true;
          drawer.removeEventListener('transitionend', onEnd);
        };
        drawer.addEventListener('transitionend', onEnd);
        window.setTimeout(() => {
          if (!drawer.classList.contains('is-open')) drawer.hidden = true;
        }, 400);
        if (lastFocused instanceof HTMLElement) lastFocused.focus();
      };

      openButton?.addEventListener('click', openDrawer);
      closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));
      applyButton?.addEventListener('click', closeDrawer);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
      });

      clearButton?.addEventListener('click', () => {
        section.querySelectorAll('[data-filter-group]:checked').forEach((input) => {
          input.checked = false;
        });
        if (minRange && maxRange) {
          minRange.value = minRange.min;
          maxRange.value = maxRange.max;
          syncInputsFromRanges();
        }
        applyFilters();
      });

      section.querySelectorAll('[data-filter-group]').forEach((input) => {
        input.addEventListener('change', applyFilters);
      });

      if (minRange && maxRange) {
        minRange.addEventListener('input', () => {
          if (Number(minRange.value) > Number(maxRange.value)) {
            maxRange.value = minRange.value;
          }
          syncInputsFromRanges();
          applyFilters();
        });
        maxRange.addEventListener('input', () => {
          if (Number(maxRange.value) < Number(minRange.value)) {
            minRange.value = maxRange.value;
          }
          syncInputsFromRanges();
          applyFilters();
        });
        syncInputsFromRanges();
      }

      if (minInput && maxInput) {
        minInput.addEventListener('change', () => {
          syncRangesFromInputs();
          applyFilters();
        });
        maxInput.addEventListener('change', () => {
          syncRangesFromInputs();
          applyFilters();
        });
      }

      sortSelect?.addEventListener('change', sortCards);

      // Firefox (reload) and Chrome/Edge (back without bfcache) restore stale form values,
      // which would silently hide cards before the shopper touches any filter.
      section.querySelectorAll('[data-filter-group]').forEach((input) => {
        input.checked = input.defaultChecked;
      });
      if (minRange && maxRange) {
        minRange.value = minRange.min;
        maxRange.value = maxRange.max;
        syncInputsFromRanges();
      }
      if (sortSelect) sortSelect.selectedIndex = 0;

      applyFilters();

      section.querySelectorAll('[data-collection-card-slideshow]').forEach((link) => {
        const images = Array.from(link.querySelectorAll('.cgrid-card__image'));
        if (images.length <= 1) {
          images[0]?.classList.add('is-active');
          return;
        }

        images[0].classList.add('is-active');
        let activeIndex = 0;
        let slideTimer = null;
        const slideIntervalMs = 900;
        const hoverQuery = window.matchMedia('(hover: hover)');

        const showSlide = (index) => {
          images.forEach((image, imageIndex) => {
            image.classList.toggle('is-active', imageIndex === index);
          });
        };

        const stopSlideshow = () => {
          if (slideTimer) {
            window.clearInterval(slideTimer);
            slideTimer = null;
          }
          activeIndex = 0;
          showSlide(0);
        };

        link.addEventListener('mouseenter', () => {
          if (!hoverQuery.matches) return;
          slideTimer = window.setInterval(() => {
            activeIndex = (activeIndex + 1) % images.length;
            showSlide(activeIndex);
          }, slideIntervalMs);
        });
        link.addEventListener('mouseleave', stopSlideshow);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
